import { useEffect, useRef, useState } from "react";
import { FileDown, Loader2, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { generatePrayerDayPDF, generatePrayerMonthPDF, generatePrayerYearPDF } from "@/lib/pdf/generate-prayer";
import { downloadPDF } from "@/lib/pdf/pdf-shared";
import type { Location } from "@/data/locations";
import { todayISO, parseISO } from "@/lib/calendar/dates";

type Mode = "day" | "month" | "year";

interface Props {
  variant: "sholat" | "imsak" | "buka-puasa";
  location: Location;
  defaultMode?: Mode;
  year?: number;
  month?: number;
  day?: number;
}

export function PrayerPdfButton({ variant, location, defaultMode = "month", year, month, day }: Props) {
  const today = parseISO(todayISO())!;
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState<Mode>(defaultMode);
  const [logo, setLogo] = useState<File | null>(null);
  const [logoMode, setLogoMode] = useState<"default" | "custom" | "none">("default");
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewBytes, setPreviewBytes] = useState<Uint8Array | null>(null);
  const previewName = useRef<string>("");

  const variantTitle =
    variant === "sholat" ? "Jadwal Sholat" :
    variant === "imsak" ? "Jadwal Imsakiyah" :
    "Jadwal Buka Puasa";

  useEffect(() => {
    if (typeof window === "undefined") return;
    const saved = window.localStorage.getItem("pdf-logo-mode");
    if (saved === "default" || saved === "custom" || saved === "none") setLogoMode(saved);
  }, []);
  useEffect(() => {
    if (typeof window !== "undefined") window.localStorage.setItem("pdf-logo-mode", logoMode);
  }, [logoMode]);

  useEffect(() => {
    return () => { if (previewUrl) URL.revokeObjectURL(previewUrl); };
  }, [previewUrl]);

  // Reset preview when key inputs change
  useEffect(() => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
      setPreviewBytes(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, logoMode, logo, variant, location.slug]);

  async function buildPdf(): Promise<{ bytes: Uint8Array; name: string }> {
    const useDefaultLogo = logoMode === "default";
    const logoFile = logoMode === "custom" ? logo : null;
    const y = year ?? today.y;
    const m = month ?? today.m;
    const d = day ?? today.d;
    const baseOpts = { variant, location, logo: logoFile, useDefaultLogo };
    let bytes: Uint8Array;
    let name: string;
    if (mode === "day") {
      bytes = await generatePrayerDayPDF(y, m, d, baseOpts);
      name = `jadwal-${variant}-${location.slug}-${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}.pdf`;
    } else if (mode === "month") {
      bytes = await generatePrayerMonthPDF(y, m, baseOpts);
      name = `jadwal-${variant}-${location.slug}-${y}-${String(m).padStart(2, "0")}.pdf`;
    } else {
      bytes = await generatePrayerYearPDF(y, baseOpts);
      name = `jadwal-${variant}-${location.slug}-${y}.pdf`;
    }
    return { bytes, name };
  }

  async function handlePreview() {
    setBusy(true);
    try {
      const { bytes, name } = await buildPdf();
      const blob = new Blob([bytes as BlobPart], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(url);
      setPreviewBytes(bytes);
      previewName.current = name;
    } catch (err) {
      console.error("Preview failed:", err);
      toast.error("Gagal membuat preview PDF.");
    } finally {
      setBusy(false);
    }
  }

  async function handleExport() {
    setBusy(true);
    try {
      // If user already previewed with same inputs, reuse those bytes.
      if (previewBytes) {
        downloadPDF(previewBytes, previewName.current);
      } else {
        const { bytes, name } = await buildPdf();
        downloadPDF(bytes, name);
      }
      setOpen(false);
    } catch (err) {
      console.error("Prayer PDF export failed:", err);
      toast.error("Gagal membuat PDF.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <FileDown className="mr-2 h-4 w-4" /> PDF
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Download {variantTitle} PDF</DialogTitle>
          <DialogDescription>Export jadwal {location.name} untuk dicetak atau disimpan.</DialogDescription>
        </DialogHeader>

        <div>
          <Label className="mb-2 block">Mode export</Label>
          <RadioGroup value={mode} onValueChange={(v) => setMode(v as Mode)} className="grid grid-cols-3 gap-2">
            <Label className="flex cursor-pointer items-center gap-2 rounded-md border border-border p-2 text-sm">
              <RadioGroupItem value="day" /> Hari ini
            </Label>
            <Label className="flex cursor-pointer items-center gap-2 rounded-md border border-border p-2 text-sm">
              <RadioGroupItem value="month" /> Sebulan
            </Label>
            <Label className="flex cursor-pointer items-center gap-2 rounded-md border border-border p-2 text-sm">
              <RadioGroupItem value="year" /> Setahun
            </Label>
          </RadioGroup>
        </div>

        <div>
          <Label className="mb-2 block">Logo</Label>
          <RadioGroup value={logoMode} onValueChange={(v) => setLogoMode(v as typeof logoMode)} className="grid gap-2">
            <Label className="flex cursor-pointer items-center gap-2 rounded-md border border-border p-2 text-sm">
              <RadioGroupItem value="default" /> Logo hari.co.id (default)
            </Label>
            <Label className="flex cursor-pointer items-center gap-2 rounded-md border border-border p-2 text-sm">
              <RadioGroupItem value="custom" /> Upload logo sendiri
            </Label>
            <Label className="flex cursor-pointer items-center gap-2 rounded-md border border-border p-2 text-sm">
              <RadioGroupItem value="none" /> Tanpa logo
            </Label>
          </RadioGroup>
          {logoMode === "custom" && (
            <Input type="file" accept="image/png,image/jpeg" className="mt-2"
              onChange={(e) => { const f = e.target.files?.[0] ?? null; if (f && f.size > 1024 * 1024) { alert("Logo terlalu besar (maks 1MB)."); return; } setLogo(f); }} />
          )}
        </div>

        <p className="rounded-md bg-prayer/10 p-3 text-xs text-prayer">
          Perhitungan menggunakan metode resmi Kementerian Agama RI. Akurasi ±1 menit terhadap jadwal SIHAT Kemenag.
        </p>

        {previewUrl && (
          <div className="rounded-md border border-border overflow-hidden">
            <div className="flex items-center justify-between bg-muted/40 px-3 py-1.5 text-xs">
              <span className="font-medium">Preview PDF — {previewName.current}</span>
              <span className="text-muted-foreground">Verifikasi sebelum download</span>
            </div>
            <iframe
              src={previewUrl}
              title="Preview PDF"
              className="h-[480px] w-full bg-white"
            />
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Batal</Button>
          <Button
            variant="outline"
            onClick={handlePreview}
            disabled={busy || (logoMode === "custom" && !logo)}
          >
            {busy && !previewUrl
              ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Menyiapkan...</>
              : <><Eye className="mr-2 h-4 w-4" /> {previewUrl ? "Generate ulang" : "Preview"}</>}
          </Button>
          <Button onClick={handleExport} disabled={busy || (logoMode === "custom" && !logo)} className="bg-prayer text-prayer-foreground hover:bg-prayer/90">
            {busy ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Menyiapkan...</>) : "Download"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
